import type { LotushealthDosespotRecordsMedicationStatusT } from '@lotushealth/lotushealth_js_sdk/schemas';

import type { Tone } from '@/shared/ui/statusTone';

export interface StatusLabel {
  readonly label: string;
  readonly tone: Tone;
}

const FALLBACK_STATUS_LABEL: StatusLabel = { label: 'Unknown', tone: 'neutral' };

const MEDICATION_STATUS_LABELS: Readonly<
  Record<LotushealthDosespotRecordsMedicationStatusT, StatusLabel>
> = {
  Active: { label: 'Active', tone: 'info' },
  Inactive: { label: 'Inactive', tone: 'muted' },
  Discontinued: { label: 'Discontinued', tone: 'muted' },
  Deleted: { label: 'Deleted', tone: 'muted' },
  Completed: { label: 'Completed', tone: 'success' },
  CancelRequested: { label: 'Cancel requested', tone: 'warning' },
  CancelPending: { label: 'Cancel pending', tone: 'warning' },
  Cancelled: { label: 'Cancelled', tone: 'destructive' },
  CancelDenied: { label: 'Cancel denied', tone: 'destructive' },
  Changed: { label: 'Changed', tone: 'info' },
  FullFill: { label: 'Full fill', tone: 'success' },
  PartialFill: { label: 'Partial fill', tone: 'warning' },
  NoFill: { label: 'No fill', tone: 'destructive' },
  Unknown: FALLBACK_STATUS_LABEL,
};

const CANCEL_STATUSES = ['CancelRequested', 'CancelPending', 'Cancelled', 'CancelDenied'] as const;
const CANCEL_STATUS_SET: ReadonlySet<string> = new Set<string>(CANCEL_STATUSES);

export type CancelStatus = (typeof CANCEL_STATUSES)[number];

export function medicationStatusLabel(
  status: LotushealthDosespotRecordsMedicationStatusT | null | undefined
): StatusLabel {
  if (status === null || status === undefined) return FALLBACK_STATUS_LABEL;
  return MEDICATION_STATUS_LABELS[status] ?? { label: String(status), tone: 'neutral' };
}

export function isCancelStatus(value: string | null | undefined): value is CancelStatus {
  return typeof value === 'string' && CANCEL_STATUS_SET.has(value);
}

export function cancelStatusLabel(
  status: LotushealthDosespotRecordsMedicationStatusT | null | undefined
): StatusLabel | null {
  return isCancelStatus(status) ? medicationStatusLabel(status) : null;
}
